export type AttendanceClockAction = 'in' | 'out';

export interface AttendanceClockRow {
  user_id?: string | number | null;
  employee_id?: string | number | null;
  clock_in_time?: string | null;
  clock_out_time?: string | null;
  out_time?: string | null;
}

export interface AttendanceClockState {
  active: boolean;
  doneForToday: boolean;
  hasClockedIn: boolean;
  hasClockedOut: boolean;
  nextAction: AttendanceClockAction | null;
}

const hasTime = (value: unknown): boolean =>
  typeof value === 'string' && value !== '' && value !== '00:00:00';

export const localToday = (date = new Date()): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const localDayOffset = (days: number, date = new Date()): string => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return localToday(shifted);
};

export const getAttendanceUserId = (user: any): string | null => {
  const id = user?.user_id ?? user?.id ?? user?.sub ?? user?.employee_id ?? null;
  return id === null || id === undefined || id === '' ? null : String(id);
};

export const getAttendanceUserIds = (user: any): string[] =>
  Array.from(
    new Set(
      [user?.user_id, user?.id, user?.sub, user?.employee_id]
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map(String),
    ),
  );

export const deriveAttendanceClockState = (
  rows: AttendanceClockRow[],
  userId: string | string[] | null,
  sessionClock: boolean,
  carryoverRows: AttendanceClockRow[] = [],
): AttendanceClockState => {
  const acceptedUserIds = new Set(
    (Array.isArray(userId) ? userId : userId ? [userId] : []).map(String),
  );
  const rowsForUser = (source: AttendanceClockRow[]) =>
    acceptedUserIds.size > 0
      ? source.filter((row) => {
          const rowUserId = row.user_id ?? row.employee_id;
          return (
            rowUserId !== null &&
            rowUserId !== undefined &&
            acceptedUserIds.has(String(rowUserId))
          );
        })
      : [];
  const mine = rowsForUser(rows);
  const activeWindow = [...rowsForUser(carryoverRows), ...mine];

  const inCount = mine.filter((row) => hasTime(row.clock_in_time)).length;
  const outCount = mine.filter((row) => hasTime(row.clock_out_time ?? row.out_time)).length;
  const activeInCount = activeWindow.filter((row) => hasTime(row.clock_in_time)).length;
  const activeOutCount = activeWindow.filter((row) => hasTime(row.clock_out_time ?? row.out_time)).length;
  const hasClockedIn = inCount > 0;
  const hasClockedOut = outCount > 0;
  const active = activeInCount > activeOutCount;

  if (sessionClock) {
    return {
      active,
      doneForToday: false,
      hasClockedIn,
      hasClockedOut,
      nextAction: active ? 'out' : 'in',
    };
  }

  // "Currently clocked in" is a punch-count comparison, not "has any open row".
  // Each clock-in / clock-out is a SEPARATE AttendanceRecord, so a multi-punch
  // day (e.g. a lunch break) can have completed clock-outs yet still end on an
  // open clock-in. Using `hasClockedOut` here wrongly marked such a day "done"
  // and disabled the sidebar Clock Out. More INs than OUTs across the carryover
  // window means the user is still clocked in.
  const doneForToday = !active && hasClockedIn;
  return {
    active,
    doneForToday,
    hasClockedIn,
    hasClockedOut,
    nextAction: doneForToday ? null : active ? 'out' : 'in',
  };
};
